import React, { useState, useEffect } from 'react';
import adminFetch from '../utils/adminFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Every Admin request goes through adminFetch (fresh Firebase token). Admin rights are decided
// server-side from that token only — admin_user_id / admin_email are no longer sent.
const q = (o) => new URLSearchParams(o).toString();

function AdminPanel({ userId, userEmail, onBack }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');  // all, pending, approved, blocked
  const [sortBy, setSortBy] = useState('newest');  // newest, oldest
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${API_BASE_URL}/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      } else if (res.status === 403) {
        setError('אין לך הרשאת Admin');
      } else if (res.status === 401) {
        setError('לא ניתן לאמת את זהותך — התחבר מחדש');
      } else {
        setError('שגיאה בטעינת המשתמשים');
      }
    } catch (err) {
      setError('שגיאה בהתחברות לשרת');
    } finally {
      setIsLoading(false);
    }
  };

  const approveUser = async (user, limit = 10) => {
    setActionLoading(user.user_id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/admin/user/approve?${q({ target_user_id: user.user_id, target_email: user.email || '', daily_limit: limit })}`,
        { method: 'POST' }
      );
      if (res.ok) {
        loadUsers();
      } else {
        alert('שגיאה באישור המשתמש');
      }
    } catch (err) {
      alert('שגיאה באישור המשתמש');
    } finally {
      setActionLoading(null);
    }
  };

  const blockUser = async (user) => {
    if (!window.confirm(`האם לחסום את ${user.email}?`)) return;
    
    setActionLoading(user.user_id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/admin/user/block?${q({ target_user_id: user.user_id, target_email: user.email || '' })}`,
        { method: 'POST' }
      );
      if (res.ok) {
        loadUsers();
      } else {
        alert('שגיאה בחסימת המשתמש');
      }
    } catch (err) {
      alert('שגיאה בחסימת המשתמש');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteUser = async (user) => {
    const name = user.email || user.name || user.user_id;
    if (!window.confirm(
      `⚠️ מחיקה מלאה של: ${name}\n\n` +
      `יימחקו:\n` +
      `• פרופיל + מצב פציעה\n` +
      `• תכניות אימון\n` +
      `• היסטוריית אימונים\n` +
      `• הודעות צ'אט\n` +
      `• חיבור Strava\n` +
      `• כל שאר הנתונים\n\n` +
      `פעולה זו לא ניתנת לביטול!`
    )) return;
    if (!window.confirm(`בטוח למחוק את ${name}?`)) return;

    if (!user.user_id) {
      alert('למשתמש זה אין user_id — לא ניתן למחוק דרך המנגנון המאובטח.');
      return;
    }

    setActionLoading(user.user_id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/admin/user/delete?${q({ target_user_id: user.user_id })}`,
        { method: 'DELETE' }
      );
      const data = await res.json().catch(() => ({}));
      const stravaNote = data.strava === 'failed'
        ? '\n\n⚠️ ביטול ההרשאה ב-Strava נכשל — המשתמש יכול להסיר את RitmoIQ בהגדרות Strava.'
        : '';
      if (res.ok && data.status === 'deleted') {
        const tables = Object.entries(data.deleted || {}).map(([t, n]) => `${t}: ${n}`).join('\n• ');
        alert(`✅ ${name} נמחק לחלוטין (כולל חשבון ההתחברות)\n\nטבלאות שנמחקו:\n• ${tables || 'אין נתונים'}${stravaNote}`);
        loadUsers();
      } else if (data.status === 'partial') {
        alert(`⚠️ הנתונים של ${name} נמחקו, אבל מחיקת חשבון ההתחברות (Firebase) נכשלה. מחק שוב כדי להשלים.${stravaNote}`);
        loadUsers();
      } else if (data.status === 'failed') {
        alert(`❌ המחיקה נכשלה ולא נמחק דבר (${data.reason || 'error'}${data.tables ? ': ' + data.tables.join(', ') : ''}).`);
      } else if (res.status === 401 || res.status === 403) {
        alert('לא ניתן לאמת הרשאת Admin — התחבר מחדש ונסה שוב. לא נמחק דבר.');
      } else {
        alert(`שגיאה במחיקת המשתמש: ${data.detail || res.status}`);
      }
    } catch (err) {
      alert('שגיאה במחיקת המשתמש');
    } finally {
      setActionLoading(null);
    }
  };

  const updateLimit = async (user, newLimit) => {
    setActionLoading(user.user_id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/admin/user/limit?${q({ target_user_id: user.user_id, target_email: user.email || '', daily_limit: newLimit })}`,
        { method: 'PUT' }
      );
      if (res.ok) {
        loadUsers();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const setCoach = async (user, makeCoach) => {
    if (!window.confirm(makeCoach ? `להגדיר את ${user.email} כמאמן?` : `להסיר את ${user.email} מרשימת המאמנים?`)) return;
    setActionLoading(user.user_id);
    try {
      const res = await adminFetch(
        `${API_BASE_URL}/admin/user/set-coach?${q({ target_user_id: user.user_id, target_email: user.email || '', target_name: user.name || '', is_coach: makeCoach })}`,
        { method: 'POST' }
      );
      if (res.ok) {
        loadUsers();
      } else {
        alert('שגיאה בעדכון סטטוס מאמן');
      }
    } catch (err) {
      alert('שגיאה בעדכון סטטוס מאמן');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users
    .filter(u => {
      if (filter === 'all') return true;
      return u.status === filter;
    })
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });

  const stats = {
    total: users.length,
    pending: users.filter(u => (u.status === 'pending' || !u.status) && !u.is_admin).length,
    approved: users.filter(u => u.status === 'approved' || u.is_admin).length,
    blocked: users.filter(u => u.status === 'blocked').length,
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  return (
    <div className="min-h-screen bg-[#0f172a]" dir="rtl">
      {/* Header */}
      <header className="sticky top-0 bg-[#0f172a]/95 backdrop-blur-sm border-b border-white/5 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center hover:bg-blue-600/30 transition"
              >
                <span className="text-xl">🏠</span>
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">🔧 ניהול מערכת</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={loadUsers}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-500 transition"
            >
              🔄 רענן
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Error */}
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`p-4 rounded-xl border transition ${
              filter === 'all' 
                ? 'bg-blue-600/20 border-blue-500/50' 
                : 'bg-[#1e293b] border-white/5 hover:border-white/20'
            }`}
          >
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-gray-500">סה"כ</p>
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`p-4 rounded-xl border transition ${
              filter === 'pending' 
                ? 'bg-yellow-600/20 border-yellow-500/50' 
                : 'bg-[#1e293b] border-white/5 hover:border-white/20'
            }`}
          >
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
            <p className="text-xs text-gray-500">ממתינים</p>
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`p-4 rounded-xl border transition ${
              filter === 'approved' 
                ? 'bg-green-600/20 border-green-500/50' 
                : 'bg-[#1e293b] border-white/5 hover:border-white/20'
            }`}
          >
            <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
            <p className="text-xs text-gray-500">מאושרים</p>
          </button>
          <button
            onClick={() => setFilter('blocked')}
            className={`p-4 rounded-xl border transition ${
              filter === 'blocked' 
                ? 'bg-red-600/20 border-red-500/50' 
                : 'bg-[#1e293b] border-white/5 hover:border-white/20'
            }`}
          >
            <p className="text-2xl font-bold text-red-400">{stats.blocked}</p>
            <p className="text-xs text-gray-500">חסומים</p>
          </button>
        </div>

        {/* Sort Control */}
        <div className="flex justify-end mb-2">
          <button
            onClick={() => setSortBy(s => s === 'newest' ? 'oldest' : 'newest')}
            className="text-xs text-gray-400 hover:text-white border border-white/10 rounded-lg px-3 py-1.5 transition"
          >
            📅 מיון: {sortBy === 'newest' ? 'חדש ראשון ↓' : 'ישן ראשון ↑'}
          </button>
        </div>

        {/* Users List */}
        {isLoading ? (
          <div className="text-center py-12">
            <span className="animate-spin text-4xl">⏳</span>
            <p className="text-gray-500 mt-4">טוען משתמשים...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-4xl">👥</span>
                <p className="text-gray-500 mt-4">אין משתמשים להצגה</p>
              </div>
            ) : (
              filteredUsers.map((user) => (
                <div
                  key={user.user_id}
                  className={`p-4 rounded-xl border transition ${
                    user.is_admin 
                      ? 'bg-purple-500/10 border-purple-500/30'
                      : user.status === 'approved'
                        ? 'bg-[#1e293b] border-green-500/20'
                        : user.status === 'blocked'
                          ? 'bg-[#1e293b] border-red-500/20'
                          : 'bg-[#1e293b] border-yellow-500/20'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-white font-medium break-words">
                          {user.email || <span className="text-yellow-400">אין אימייל</span>}
                        </p>
                        {user.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">Admin</span>
                        )}
                        {user.is_coach && (
                          <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">🏋️ מאמן</span>
                        )}
                        {user.status === 'approved' && !user.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">מאושר</span>
                        )}
                        {user.status === 'blocked' && (
                          <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs">חסום</span>
                        )}
                        {(!user.status || user.status === 'pending') && !user.is_admin && (
                          <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs animate-pulse">ממתין</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm">
                        {user.name || 'ללא שם'} • ID: {user.user_id?.substring(0, 10)}...
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span className="text-cyan-500/80">📅 {formatDate(user.created_at)}</span>
                        <span>💬 הודעות: {user.total_messages}</span>
                        <span style={{color: user.today_usage > 0 ? '#34d399' : '#64748b'}}>
                          ⚡ היום: {user.today_usage || 0}/{user.daily_limit || 10}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {!user.is_admin && (
                      <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                        {actionLoading === user.user_id ? (
                          <span className="animate-spin">⏳</span>
                        ) : (
                          <>
                            {user.status !== 'approved' && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-400 ml-1">אשר:</span>
                                <button
                                  onClick={() => approveUser(user, 5)}
                                  className="px-2 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-500 transition"
                                >
                                  5
                                </button>
                                <button
                                  onClick={() => approveUser(user, 10)}
                                  className="px-2 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-500 transition font-bold"
                                >
                                  10
                                </button>
                                <button
                                  onClick={() => approveUser(user, 20)}
                                  className="px-2 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-500 transition"
                                >
                                  20
                                </button>
                                <button
                                  onClick={() => approveUser(user, 50)}
                                  className="px-2 py-1 rounded-lg bg-green-600 text-white text-xs hover:bg-green-500 transition"
                                >
                                  50
                                </button>
                              </div>
                            )}
                            {user.status === 'approved' && (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-400 ml-1">מגבלה:</span>
                                <select
                                  value={[5,10,20,50,100,200,500].includes(user.daily_limit) ? user.daily_limit : 'custom'}
                                  onChange={(e) => {
                                    if (e.target.value === 'custom') {
                                      const v = window.prompt(`מגבלה יומית חדשה ל-${user.email}:`, user.daily_limit || 10);
                                      const n = parseInt(v, 10);
                                      if (!isNaN(n) && n > 0 && n <= 9999) updateLimit(user, n);
                                    } else {
                                      updateLimit(user, parseInt(e.target.value));
                                    }
                                  }}
                                  className="px-2 py-1.5 rounded-lg bg-[#0f172a] border border-green-500/30 text-white text-sm font-medium"
                                >
                                  <option value="5">5/יום</option>
                                  <option value="10">10/יום</option>
                                  <option value="20">20/יום</option>
                                  <option value="50">50/יום</option>
                                  <option value="100">100/יום</option>
                                  <option value="200">200/יום</option>
                                  <option value="500">500/יום</option>
                                  <option value="custom">✏️ מותאם...{[5,10,20,50,100,200,500].includes(user.daily_limit) ? '' : ` (${user.daily_limit})`}</option>
                                </select>
                              </div>
                            )}
                            {user.status !== 'blocked' && (
                              <button
                                onClick={() => blockUser(user)}
                                className="px-3 py-1.5 rounded-lg bg-red-600/20 text-red-400 text-sm hover:bg-red-600/30 transition"
                              >
                                🚫
                              </button>
                            )}
                            {user.status === 'blocked' && (
                              <button
                                onClick={() => approveUser(user, 10)}
                                className="px-3 py-1.5 rounded-lg bg-green-600/20 text-green-400 text-sm hover:bg-green-600/30 transition"
                              >
                                בטל חסימה
                              </button>
                            )}
                            {/* Coach button */}
                            {!user.is_coach ? (
                              <button
                                onClick={() => setCoach(user, true)}
                                className="px-3 py-1.5 rounded-lg bg-cyan-600/20 text-cyan-400 text-xs hover:bg-cyan-600/30 transition"
                                title="הגדר כמאמן"
                              >
                                🏋️ מאמן
                              </button>
                            ) : (
                              <button
                                onClick={() => setCoach(user, false)}
                                className="px-3 py-1.5 rounded-lg bg-red-600/20 text-red-400 text-xs hover:bg-red-600/30 transition"
                                title="הסר מאמן"
                              >
                                ❌ הסר מאמן
                              </button>
                            )}
                            {/* Delete button */}
                            <button
                              onClick={() => deleteUser(user)}
                              className="px-3 py-1.5 rounded-lg bg-gray-600/20 text-gray-400 text-sm hover:bg-red-600/30 hover:text-red-400 transition"
                              title="מחק משתמש לחלוטין"
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPanel;
